import classNames from "classnames";
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { getPreSignedUrl, uploadFilesTos3 } from "src/services/upload";
import { Spinner, Label } from "reactstrap";
import { cloneFile, getFileName, getFileType, isDuplicateFileName } from "src/utils/common";
import { FILE_TYPE, SIMULATION_TYPE } from "src/constant/common";
import { UPLOAD_TYPE } from "src/constant/common";
import { useRecoilState, useSetRecoilState } from "recoil";
import { uploadedFilesState } from "src/recoil/atoms/uploadedFilesState";
import { uploadingFile } from "src/recoil/atoms/global";
import { uploadedFiles6DOFState } from "src/recoil/atoms/flightSimulator";
import { FORM_NAME } from "../Modal/ModalUploadFile6DOF";
import TooltipWrapper from "src/components/Common/TooltipWrapper";

const UploadFileDraggable = ({
  name,
  placeholder,
  classInput,
  classGroup,
  setValue,
  register,
  clearErrors,
  errors,
  required,
  requiredMessage,
  label,
  uploadType,
  fileTypeAllow = [FILE_TYPE.JSON, FILE_TYPE.CSV, FILE_TYPE.XLSX],
  isMultiple = true,
  initialFiles = [],
  onDeleteFile,
  disabled = false,
  checkDuplicatedFileNames = false,
  ignoreUploadS3 = false,
  simulationType = SIMULATION_TYPE["3DOF"],
  isSubmitting = false,
  sameFileTypeRequired = false,
}) => {
  const inputRef = useRef(null);
  const [globalUploadingField, setGlobalUploadingField] = useRecoilState(uploadingFile);
  const setUploadedFiles = useSetRecoilState(uploadedFilesState);
  const setUploadedFiles6DOF = useSetRecoilState(uploadedFiles6DOFState);
  const { t } = useTranslation(["flight-simulator", "common"]);
  const [toggle, setToggle] = useState(false);
  const [itemDelete, setItemDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    setFiles(initialFiles);
  }, [initialFiles]);

  useEffect(() => {
    if (errors[name] && inputRef.current) {
      inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errors, name, isSubmitting, inputRef]);

  const getDuplicateFileNameErrorMessage = (files, initialFiles) => {
    const fileUploadedDuplicate = files.filter((file, index) => {
      return files.findIndex((f) => f.name === file.name) !== index;
    });

    if (fileUploadedDuplicate.length > 0) {
      return t("new.validate_duplicate_file_name_in_files_upload");
    }

    const duplicateFiles = files.filter((file) => isDuplicateFileName(initialFiles, file.name));

    if (duplicateFiles.length > 0) {
      return t("new.validate_duplicate_file_name");
    }
  };

  const acceptFiles = () => fileTypeAllow.map((item) => `.${item}`).join();
  const handleSaveFilesUploaded = (fileData, type) => {
    if (type === SIMULATION_TYPE["3DOF"]) {
      setUploadedFiles((prev) => {
        const updatedFiles = [...prev];

        fileData.forEach((newFile) => {
          updatedFiles.push(newFile);
        });

        return updatedFiles;
      });
      return;
    }
    if (type === SIMULATION_TYPE["6DOF"]) {
      setUploadedFiles6DOF((prev) => {
        const updatedFiles = [...prev];

        fileData.forEach((newFile) => {
          if (name === FORM_NAME.TARGET_ATTITUDE_ASCENT) {
            updatedFiles.push({ [FORM_NAME.TARGET_ATTITUDE_ASCENT]: newFile });
            return;
          }
          if (name === FORM_NAME.TARGET_ATTITUDE_DESCENT) {
            updatedFiles.push({ [FORM_NAME.TARGET_ATTITUDE_DESCENT]: newFile });
            return;
          }
          if (name === FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT) {
            updatedFiles.push({ [FORM_NAME.TARGET_ATTITUDE_RATE_ASCENT]: newFile });
            return;
          }
          if (name === FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT) {
            updatedFiles.push({ [FORM_NAME.TARGET_ATTITUDE_RATE_DESCENT]: newFile });
            return;
          }

          updatedFiles.push(newFile);
        });

        return updatedFiles;
      });
      return;
    }
  };
  const handleUploadFile = async (e) => {
    if (disabled || globalUploadingField) return;

    if (e?.target?.files?.length > 10) {
      clearErrors(name);
      setErrMsg(t("new.limit_attachments"));
      return;
    }
    if (checkDuplicatedFileNames) {
      const files = Array.from(e?.target?.files);
      const errorMsg = getDuplicateFileNameErrorMessage(files, initialFiles);
      if (errorMsg) {
        return setErrMsg(errorMsg);
      }
    }

    if (sameFileTypeRequired && fileTypeAllow.length > 1 && e?.target?.files?.length > 1) {
      const filesArray = Array.from(e?.target?.files);
      const firstFileType = getFileType(filesArray[0].name);
      const hasDifferentTypes = filesArray.some((file) => getFileType(file.name) !== firstFileType);

      if (hasDifferentTypes) {
        clearErrors(name);
        setErrMsg(t("new.validate_same_file_type_required"));
        return;
      }
    }

    const fileData = [];

    [...e?.target?.files].map((item) => {
      if (!fileTypeAllow.includes(getFileType(item.name))) {
        clearErrors(name);
        return setErrMsg(
          t("new.validate_attachments_custom", {
            file_type: fileTypeAllow.join(", "),
          })
        );
      } else {
        const fileClone = cloneFile(item);
        return fileData.push({
          file: fileClone,
          name: fileClone?.name,
          size: fileClone?.size,
          fieldName: name,
          uploadedAt: Date.now(),
        });
      }
    });
    if (fileData.length > 0) {
      setUploading(true);
      setGlobalUploadingField(true);
      setErrMsg("");

      if (ignoreUploadS3) {
        handleSaveFilesUploaded(fileData, simulationType);

        // Just use the file names without uploading to S3
        const fileObjects = fileData.map((file) => {
          return { name: file?.name, size: file?.size };
        });
        setValue(fileObjects);
        setFiles(fileObjects);
        setUploading(false);
        setGlobalUploadingField(false);
        return;
      }

      Promise.all([...fileData].map(uploadFile))
        .then((res) => {
          setValue(res);
          setFiles(res);
        })
        .finally(() => {
          setUploading(false);
          setGlobalUploadingField(false);
        });
    }
  };

  const handleDragEnter = useCallback(
    (e) => {
      if (disabled || globalUploadingField) return;
      e.preventDefault();
      e.stopPropagation();
      setDragOver(true);
    },
    [disabled, globalUploadingField]
  );

  const handleDragLeave = useCallback(
    (e) => {
      if (disabled || globalUploadingField) return;
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
    },
    [disabled, globalUploadingField]
  );

  const handleDragOver = useCallback(
    (e) => {
      if (disabled || globalUploadingField) return;
      e.preventDefault();
      e.stopPropagation();
      if (!dragOver) {
        setDragOver(true);
      }
    },
    [dragOver, disabled, globalUploadingField]
  );

  const handleDrop = useCallback(
    (e) => {
      if (disabled || globalUploadingField) return;
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const droppedFiles = Array.from(e.dataTransfer.files);

        if (droppedFiles.length > 10) {
          clearErrors(name);
          return setErrMsg(t("new.limit_attachments"));
        }

        if (checkDuplicatedFileNames) {
          const files = Array.from(e.dataTransfer.files);
          const errorMsg = getDuplicateFileNameErrorMessage(files, initialFiles);
          if (errorMsg) {
            return setErrMsg(errorMsg);
          }
        }

        if (!isMultiple && droppedFiles.length > 1) {
          clearErrors(name);
          return setErrMsg(t("new.limit_single_file"));
        }

        if (sameFileTypeRequired && fileTypeAllow.length > 1 && droppedFiles.length > 1) {
          const firstFileType = getFileType(droppedFiles[0].name);
          const hasDifferentTypes = droppedFiles.some((file) => getFileType(file.name) !== firstFileType);

          if (hasDifferentTypes) {
            clearErrors(name);
            return setErrMsg(t("new.validate_same_file_type_required"));
          }
        }

        const validFiles = [];
        const invalidFiles = [];

        droppedFiles.forEach((file) => {
          if (!fileTypeAllow.includes(getFileType(file.name))) {
            invalidFiles.push(file.name);
          } else {
            const fileClone = cloneFile(file);
            validFiles.push({
              file: fileClone,
              uploadedAt: Date.now(),
              name: fileClone?.name,
              size: fileClone?.size,
              fieldName: name,
            });
          }
        });

        if (invalidFiles.length > 0) {
          clearErrors(name);
          return setErrMsg(
            t("new.validate_attachments_custom", {
              file_type: fileTypeAllow.join(", "),
            })
          );
        }

        setErrMsg("");
        setUploading(true);
        setGlobalUploadingField(true);

        if (ignoreUploadS3) {
          handleSaveFilesUploaded(validFiles, simulationType);

          const fileObjects = validFiles.map((file) => {
            return { name: file?.name, size: file?.size };
          });
          setValue(fileObjects);
          setFiles(fileObjects);
          setUploading(false);
          setGlobalUploadingField(false);
          e.dataTransfer.clearData();
          return;
        }

        Promise.all(validFiles.map(uploadFile))
          .then((res) => {
            setValue((prev) => (isMultiple ? [...(prev || []), ...res] : res[0]));
            setFiles((prev) => (isMultiple ? [...prev, ...res] : [res[0]]));
          })
          .finally(() => {
            setUploading(false);
            setGlobalUploadingField(false);
            e.dataTransfer.clearData();
          });
      }
    },
    [disabled, globalUploadingField, ignoreUploadS3, fileTypeAllow, isMultiple, clearErrors, setErrMsg, setValue, setFiles]
  );

  const uploadFile = async (file) => {
    const presignedUrl = await getPreSignedUrl({
      data: {
        file_name: file?.name,
        type: uploadType || UPLOAD_TYPE.RAW,
      },
    });

    const { url, fields } = presignedUrl;

    return await uploadFilesTos3({
      url,
      fields,
      file,
    });
  };

  const handleDeleteAttachments = (fileToDelete) => {
    if (disabled || globalUploadingField) return;

    const newFiles = files.filter((file) => file !== fileToDelete);
    setFiles(newFiles);

    onDeleteFile(newFiles, fileToDelete);

    setItemDelete(null);
    setToggle(false);
  };

  return (
    <div className={classNames("form-group position-relative", classGroup)} ref={inputRef}>
      {label && (
        <Label for={name} className={classNames("position-relative", { "required-input": required })}>
          {label}
        </Label>
      )}
      {files?.map((item, index) => (
        <div className="card shadow-none border width-by-percent-100 m-b-7" key={`${name}-${index}`}>
          <div className="p-2 d-flex justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center overflow-hidden">
              <div className="col-auto">
                <div className="avatar-sm">
                  <span className="avatar-title bg-soft-warning text-warning rounded fs-24">
                    <i className="bi bi-file-earmark-zip"></i>
                  </span>
                </div>
              </div>
              <TooltipWrapper
                tooltipText={item?.name ? getFileName(item?.name) : item}
                id={`${name}-file-name`}
                checkOverflow={true}
              >
                <div className="text-truncate ps-1 text-muted fw-bold fs-15">
                  {item?.name ? getFileName(item?.name) : item}
                </div>
              </TooltipWrapper>
            </div>
            {!disabled && (
              <div>
                <i
                  className="bi bi-trash text-danger f-s-24 user-select-pointer"
                  role="button"
                  onClick={() => {
                    document.body.click();
                    setToggle(true);
                    setItemDelete(item);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      ))}

      {!files.length && (
        <>
          <label
            htmlFor={name}
            className={classNames("w-100 btn-upload-lg", { "disabled pe-none": disabled || globalUploadingField })}
            disabled={disabled || globalUploadingField}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="p-2 d-flex align-items-center justify-content-center">
                <Spinner color="secondary" />
              </div>
            ) : (
              <>
                <i className="bi bi-cloud-upload fs-24"></i>
                <div className="text-break-space text-center cursor-pointer">{t("new.upload_placeholder")}</div>
                <div className="btn btn-primary btn-upload-sm p-d-2 m-t-10 cursor-pointer">{t("new.upload_button")}</div>
              </>
            )}
          </label>

          <input
            {...register(name, {
              required: required,
            })}
            draggable
            multiple={isMultiple}
            type="file"
            name={name}
            id={name}
            hidden
            placeholder={placeholder}
            className={classNames("form-control cursor-pointer", classInput)}
            onChange={handleUploadFile}
            accept={acceptFiles()}
            onClick={(event) => (event.currentTarget.value = null)}
            disabled={disabled || globalUploadingField || (!isMultiple && files.length)}
            required={required}
          />
          {errors[name] && <div className="text-danger font-12 position-absolute">{requiredMessage}</div>}
          {errMsg && !errors[name] && <div className="text-danger font-12 position-absolute">{errMsg}</div>}
        </>
      )}

      <Modal isOpen={toggle} toggle={() => setToggle(false)} backdrop="static">
        <ModalHeader>{t("new.delete_modal.title_attachments")}</ModalHeader>
        <ModalBody>
          <div>{t("new.delete_modal.sure")}</div>
          <div>{t("new.delete_modal.recover")}</div>
        </ModalBody>
        <ModalFooter>
          <span className="btn btn-secondary btn-sm" role="button" onClick={() => setToggle(false)}>
            {t("new.delete_modal.cancel")}
          </span>
          <span className="btn btn-danger btn-sm" role="button" onClick={() => handleDeleteAttachments(itemDelete)}>
            {t("new.delete_modal.delete")}
          </span>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default UploadFileDraggable;
